import { useState, useEffect, useRef } from 'react'
import { useVoting, VoteOption } from '../../hooks/useVoting'
import { useCountdown } from '../../hooks/useCountdown'
import VoteButton from './VoteButton'
import ResultsDisplay from './ResultsDisplay'
import ShareButton from './ShareButton'
import { useAccount, useChainId, useSwitchChain, useConnect } from 'wagmi'
import { EXPECTED_CHAIN_ID, EXPECTED_CHAIN_NAME, BASE_MAINNET_CHAIN_ID } from '../../utils/constants'

export default function VotingCard() {
  const { address, isConnected } = useAccount()
  const chainId = useChainId()
  const { switchChain } = useSwitchChain()
  const { connectAsync, connectors } = useConnect()
  const { vote, hasVotedToday, stats, isVoting, isVoteSuccess, error: contractError, streak, reset: resetVoting } = useVoting()
  const countdown = useCountdown()
  const [selectedOption, setSelectedOption] = useState<VoteOption | null>(null)
  const [userVote, setUserVote] = useState<VoteOption | null>(null)
  const [voteError, setVoteError] = useState<string | null>(null)
  const connectAttempted = useRef(false)

  // Check if on correct network (dynamically based on environment)
  const isOnCorrectNetwork = chainId === EXPECTED_CHAIN_ID

  // Auto-connect when we're inside Farcaster or an embedded wallet is available
  useEffect(() => {
    if (connectAttempted.current || isConnected || connectors.length === 0) return

    const hasEmbeddedWallet = typeof window !== 'undefined' && (window as any).ethereum
    const farcasterConnector = connectors.find((c) => c.id?.toLowerCase().includes('farcaster') && c.ready)
    const readyConnectors = connectors.filter((c) => c.ready)

    // Only attempt if we detect Farcaster, an embedded wallet, or a ready connector
    if (!farcasterConnector && !hasEmbeddedWallet && readyConnectors.length === 0) return

    connectAttempted.current = true

    const preferredConnector =
      farcasterConnector ||
      readyConnectors.find((c) => c.type === 'injected') ||
      readyConnectors[0] ||
      connectors[0]

    if (!preferredConnector) {
      connectAttempted.current = false
      return
    }

    const attemptConnect = async () => {
      try {
        console.log('🔗 Auto-connecting with connector:', preferredConnector.name)
        await connectAsync({ connector: preferredConnector })
      } catch (error) {
        console.log('ℹ️ Auto-connect failed:', error)
        connectAttempted.current = false
      }
    }

    attemptConnect()
  }, [isConnected, connectAsync, connectors])

  // Load user's vote from localStorage on mount
  useEffect(() => {
    if (!address) {
      setUserVote(null)
      return
    }

    // Get today's date key (same format as streak tracking)
    const today = new Date()
    const dateKey = `${today.getUTCFullYear()}-${String(today.getUTCMonth() + 1).padStart(2, '0')}-${String(today.getUTCDate()).padStart(2, '0')}`

    // Check localStorage for today's vote
    const voteKey = `basevibe_today_vote_${address}_${dateKey}`
    const storedVote = localStorage.getItem(voteKey)

    if (storedVote !== null) {
      setUserVote(parseInt(storedVote) as VoteOption)
    }
  }, [address])

  const handleVote = async (option: VoteOption) => {
    if (hasVotedToday || isVoting || !address) return

    // Clear any previous errors and reset transaction state
    setVoteError(null)
    resetVoting()

    try {
      setSelectedOption(option)
      await vote(option)

      // Save the user's vote choice to state and localStorage
      setUserVote(option)

      // Save to localStorage with today's date
      const today = new Date()
      const dateKey = `${today.getUTCFullYear()}-${String(today.getUTCMonth() + 1).padStart(2, '0')}-${String(today.getUTCDate()).padStart(2, '0')}`
      const voteKey = `basevibe_today_vote_${address}_${dateKey}`
      localStorage.setItem(voteKey, option.toString())
    } catch (error: any) {
      console.error('Vote error:', error)

      // Parse and display user-friendly error
      let errorMessage = 'Failed to submit vote. Please try again.'

      if (error?.message?.includes('rejected')) {
        errorMessage = 'Transaction was rejected. Please try again.'
      } else if (error?.message?.includes('insufficient')) {
        errorMessage = 'Insufficient ETH for gas fees. Please add more ETH to your wallet.'
      } else if (error?.message?.includes('Already voted')) {
        errorMessage = 'You have already voted today. Come back tomorrow!'
      } else if (error?.message?.includes('User rejected') || error?.message?.includes('User denied')) {
        errorMessage = 'Transaction was rejected by user.'
      } else if (error?.message?.includes('gas')) {
        errorMessage = 'Gas estimation failed. Please try again or check your wallet balance.'
      }

      setVoteError(errorMessage)
      setSelectedOption(null)
      setUserVote(null)

      // Clear error after 10 seconds
      setTimeout(() => setVoteError(null), 10000)
    }
  }

  // Handle contract errors from wagmi
  useEffect(() => {
    if (contractError) {
      let errorMessage = 'Transaction failed. Please try again.'

      const errorStr = contractError.toString()
      if (errorStr.includes('rejected') || errorStr.includes('User rejected') || errorStr.includes('User denied')) {
        errorMessage = 'Transaction was rejected by user.'
      } else if (errorStr.includes('insufficient')) {
        errorMessage = 'Insufficient ETH for transaction.'
      } else if (errorStr.includes('gas')) {
        errorMessage = 'Gas estimation failed. Please check your wallet balance.'
      } else if (errorStr.includes('Already voted')) {
        errorMessage = 'You have already voted today.'
      } else if (errorStr.includes('network') || errorStr.includes('RPC')) {
        errorMessage = 'Network error. Please check your connection and try again.'
      }

      setVoteError(errorMessage)
      setSelectedOption(null) // Clear selected option on error

      // Clear error and reset transaction state after 10 seconds
      setTimeout(() => {
        setVoteError(null)
        resetVoting()
      }, 10000)
    }
  }, [contractError, resetVoting])

  // Update userVote when hasVotedToday becomes true
  useEffect(() => {
    if (hasVotedToday && !userVote && selectedOption !== null && address) {
      setUserVote(selectedOption)

      // Also save to localStorage
      const today = new Date()
      const dateKey = `${today.getUTCFullYear()}-${String(today.getUTCMonth() + 1).padStart(2, '0')}-${String(today.getUTCDate()).padStart(2, '0')}`
      const voteKey = `basevibe_today_vote_${address}_${dateKey}`
      localStorage.setItem(voteKey, selectedOption.toString())
    }
  }, [hasVotedToday, userVote, selectedOption, address])

  // Reset transaction state when vote is successful to prevent stale state
  useEffect(() => {
    if (isVoteSuccess && hasVotedToday) {
      // Wait a bit to ensure the success message is shown, then reset
      const timer = setTimeout(() => {
        resetVoting()
      }, 3000)
      return () => clearTimeout(timer)
    }
  }, [isVoteSuccess, hasVotedToday, resetVoting])

  if (!isConnected) {
    return (
      <div className="max-w-md mx-auto p-4 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-center">
        <p className="text-sm text-gray-600 dark:text-gray-300">
          Please connect your wallet to vote.
        </p>
      </div>
    )
  }

  // Show network switch prompt if on wrong network
  if (isConnected && !isOnCorrectNetwork) {
    // Determine which network details to show
    const isMainnet = EXPECTED_CHAIN_ID === BASE_MAINNET_CHAIN_ID
    const networkDetails = isMainnet ? {
      name: 'Base',
      rpcUrl: 'https://mainnet.base.org',
      chainId: '8453',
      explorer: 'https://basescan.org',
    } : {
      name: 'Base Sepolia',
      rpcUrl: 'https://sepolia.base.org',
      chainId: '84532',
      explorer: 'https://sepolia.basescan.org',
    }

    return (
      <div className="max-w-md mx-auto p-4 rounded-xl border border-orange-200 dark:border-orange-700 bg-orange-50 dark:bg-orange-900/20">
        <h3 className="text-base font-semibold text-orange-800 dark:text-orange-200 mb-2">
          Wrong Network
        </h3>
        <p className="text-sm text-orange-700 dark:text-orange-300 mb-3">
          Please switch to {EXPECTED_CHAIN_NAME} to vote.
        </p>
        <button
          onClick={() => switchChain({ chainId: EXPECTED_CHAIN_ID })}
          className="w-full px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors font-medium"
        >
          Switch to {EXPECTED_CHAIN_NAME}
        </button>
        <div className="mt-4 text-xs text-orange-600 dark:text-orange-400 space-y-1">
          <p className="font-medium">Can't switch automatically?</p>
          <p>Manually add {networkDetails.name} network:</p>
          <ul className="list-disc list-inside text-left mt-2 space-y-1">
            <li>Network Name: <strong>{networkDetails.name}</strong></li>
            <li>RPC URL: <strong>{networkDetails.rpcUrl}</strong></li>
            <li>Chain ID: <strong>{networkDetails.chainId}</strong></li>
            <li>Currency Symbol: <strong>ETH</strong></li>
            <li>Block Explorer: <strong>{networkDetails.explorer}</strong></li>
          </ul>
        </div>
      </div>
    )
  }

  // Check if contract is deployed - using the same logic as constants.ts
  const contractAddress = import.meta.env.VITE_CONTRACT_ADDRESS_SEPOLIA ||
    import.meta.env.VITE_CONTRACT_ADDRESS_MAINNET ||
    '0x0000000000000000000000000000000000000000'

  if (contractAddress === '0x0000000000000000000000000000000000000000') {
    return (
      <div className="max-w-2xl mx-auto space-y-6">
        <div className="p-6 rounded-2xl border border-yellow-500/30 bg-yellow-900/10 backdrop-blur-sm">
          <h3 className="text-lg font-semibold text-yellow-500 mb-2">
            Contract Not Deployed
          </h3>
          <p className="text-sm text-yellow-200/80 mb-4">
            To enable voting, you need to deploy the BaseVibe contract to Base Sepolia.
          </p>
          <div className="text-sm text-yellow-200/80 space-y-2">
            <p className="font-medium text-white">Quick Steps:</p>
            <ol className="list-decimal list-inside space-y-1 ml-2">
              <li>Deploy the contract (see <code className="bg-yellow-500/20 px-1 rounded text-yellow-200">SETUP_TESTNET.md</code> or use Remix)</li>
              <li>Create a <code className="bg-yellow-500/20 px-1 rounded text-yellow-200">.env</code> file in the root directory</li>
              <li>Add: <code className="bg-yellow-500/20 px-1 rounded text-yellow-200">VITE_CONTRACT_ADDRESS_SEPOLIA=0x...your_address...</code></li>
              <li>Restart the dev server</li>
            </ol>
          </div>
        </div>

        {/* Show UI preview even without contract */}
        <div className="glass-card rounded-3xl p-6 relative overflow-hidden opacity-50 pointer-events-none">
          {/* Decorative background blob */}
          <div className="absolute -top-20 -right-20 w-40 h-40 bg-purple-500/20 rounded-full blur-3xl pointer-events-none"></div>
          <div className="absolute -bottom-20 -left-20 w-40 h-40 bg-blue-500/20 rounded-full blur-3xl pointer-events-none"></div>

          <div className="relative z-10 text-center space-y-6">
            <div>
              <h2 className="text-2xl font-bold mb-2">How's the market?</h2>
              <p className="text-gray-400 text-sm">Cast your daily vote to earn rewards</p>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="flex flex-col items-center justify-center p-4 h-32 rounded-2xl bg-gradient-to-br from-emerald-500/10 to-teal-500/10 border border-emerald-500/20">
                <span className="text-4xl mb-3">🚀</span>
                <span className="font-bold text-emerald-400">Mooning</span>
              </div>
              <div className="flex flex-col items-center justify-center p-4 h-32 rounded-2xl bg-gradient-to-br from-rose-500/10 to-orange-500/10 border border-rose-500/20">
                <span className="text-4xl mb-3">🥀</span>
                <span className="font-bold text-rose-400">Rekt</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="max-w-2xl mx-auto space-y-3">
      {/* Voting Section */}
      {!hasVotedToday ? (
        <div className="glass-card rounded-3xl p-6 relative overflow-hidden transition-all duration-300">
          {/* Decorative background blob */}
          <div className="absolute -top-20 -right-20 w-40 h-40 bg-purple-500/20 rounded-full blur-3xl pointer-events-none"></div>
          <div className="absolute -bottom-20 -left-20 w-40 h-40 bg-blue-500/20 rounded-full blur-3xl pointer-events-none"></div>

          <div className="relative z-10">
            <h2 className="text-xl font-bold text-center mb-1 text-white">
              How's the market?
            </h2>
            <p className="text-gray-400 text-center text-sm mb-6">
              Cast your daily vote to earn rewards
            </p>

            <div className="grid grid-cols-2 gap-4 mb-2">
              <VoteButton
                emoji="🚀"
                label="Mooning"
                option={VoteOption.Positive}
                onClick={handleVote}
                disabled={isVoting || isVoteSuccess}
                isSelected={selectedOption === VoteOption.Positive}
              />
              <VoteButton
                emoji="🥀"
                label="Rekt"
                option={VoteOption.Negative}
                onClick={handleVote}
                disabled={isVoting || isVoteSuccess}
                isSelected={selectedOption === VoteOption.Negative}
              />
            </div>

            {/* Error Display */}
            {voteError && (
              <div className="mb-2 p-2 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800">
                <div className="flex items-start gap-2">
                  <span className="text-red-600 dark:text-red-400 text-lg flex-shrink-0">⚠️</span>
                  <div className="flex-1">
                    <p className="text-xs font-medium text-red-800 dark:text-red-200">
                      {voteError}
                    </p>
                    <div className="mt-1 flex gap-2">
                      <button
                        onClick={() => {
                          setVoteError(null)
                          resetVoting()
                        }}
                        className="text-xs text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-200 underline"
                      >
                        Dismiss
                      </button>
                      <button
                        onClick={() => {
                          setVoteError(null)
                          resetVoting()
                          setSelectedOption(null)
                        }}
                        className="text-xs text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-200 underline font-medium"
                      >
                        Try Again
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {isVoting && (
              <p className="text-center text-sm text-blue-600 dark:text-blue-400">
                Processing your vote...
              </p>
            )}

            {isVoteSuccess && (
              <div className="text-center space-y-2">
                <p className="text-sm text-green-600 dark:text-green-400 font-medium">
                  ✓ Vote recorded successfully!
                </p>
                <ShareButton stats={stats} userVote={selectedOption} />
              </div>
            )}
          </div>
        </div>
      ) : (
        <div className="glass-card rounded-3xl p-6 relative overflow-hidden">
          {/* Decorative background blob */}
          <div className="absolute -top-20 -right-20 w-40 h-40 bg-purple-500/20 rounded-full blur-3xl pointer-events-none"></div>
          <div className="absolute -bottom-20 -left-20 w-40 h-40 bg-blue-500/20 rounded-full blur-3xl pointer-events-none"></div>

          <div className="relative z-10 text-center">
            <div className="text-5xl mb-3 animate-float inline-block">
              {userVote !== null ? (userVote === VoteOption.Positive ? '🚀' : '🥀') : '✓'}
            </div>
            <h3 className="text-xl font-bold mb-1 text-white">
              <span className="gradient-text">
                {userVote !== null
                  ? (userVote === VoteOption.Positive ? 'To the Moon!' : 'Rekt')
                  : "You've voted today!"}
              </span>
            </h3>
            <p className="text-sm text-gray-400 mb-6">
              {userVote !== null
                ? `You're feeling ${userVote === VoteOption.Positive ? 'bullish' : 'bearish'} today`
                : 'Your vote has been recorded'}
            </p>

            {streak > 1 && (
              <div className="inline-flex items-center gap-2 bg-orange-500/10 border border-orange-500/30 px-4 py-2 rounded-full mb-6">
                <span className="text-xl animate-pulse">🔥</span>
                <span className="text-sm font-bold text-orange-400">{streak} day streak!</span>
              </div>
            )}

            <div className="text-sm text-gray-400 space-y-2 bg-white/5 rounded-2xl p-4 border border-white/5">
              <p className="text-xs uppercase tracking-wider font-semibold text-gray-500">Next vote available in</p>
              <p className="text-2xl font-mono font-bold text-blue-400 tabular-nums">
                {String(countdown.hours).padStart(2, '0')}h {String(countdown.minutes).padStart(2, '0')}m {String(countdown.seconds).padStart(2, '0')}s
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Results Display */}
      <ResultsDisplay stats={stats} userVote={userVote} />
    </div>
  )
}




